import React, { Dispatch, useCallback, useContext, useEffect, useState } from 'react';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Collapse from '@material-ui/core/Collapse';
import ExpandMore from '@material-ui/icons/ExpandMore';
import { AccountCircleOutlined, Add, ExitToAppOutlined, OpenInNew } from '@material-ui/icons';
import { AppDispatchContext, AppStateContext } from '../../App';
import { Divider, IconButton } from "@material-ui/core";
import { Link, useHistory } from 'react-router-dom';
import ShopSwitcherLogoOrInitials from '../ShopSwitcher/ShopSwitcherLogoOrInitials';
import classNames from "classnames";
import type { iShopifyShop } from "../../types/internal";
import { iStoreAction } from '../../redux/reducers';
import UserAPIs, { invalidateUserSignatureCache } from '../../apis/user.apis';
import CommonApis from '../../apis/common.apis';
import Axios from 'axios';
import logo from "../../assets/images/logo-tilted-small.png";
import useIsMounted from 'ismounted';
import { NotificationPopupOver, ProduktlyFeedback, ProduktlyNew } from '../../pages/Header';
import LanguageMenu from '../i18n/LanguageMenu';
import { useTranslation } from 'react-i18next';
import { clearProductCache } from '../../pages/Dashboard/MicroComponent/MicroComp';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      height: '100svh',
      width: '100%',
      backgroundColor: 'transparent',
      boxShadow: 'none',
    },
    contentHead: {
      margin: 0,
      padding: theme.spacing(0),
    },
    content: {
      flexGrow: 1,
      overflowY: 'auto',
      margin: 0,
      padding: theme.spacing(0),
      minHeight: 0,
    },
    shopListCollapseInner: {
      margin: 0,
      padding: theme.spacing(0, 1.75, 1),
      flexShrink: 0,
    },
    collapseRoot: {
      flexShrink: 0,
    },
    footer: {
      position: 'sticky',
      bottom: 0,
      width: '100%',
    },
    logoImage: {
      maxHeight: 38,
      maxWidth: 160,
      width: '75%',
      height: 'auto',
      objectFit: 'contain',
      display: 'block',
      margin: '0 auto',
    },
  }),
);

export default function DrawerLayout({
  children,
  onRequestClose,
}: {
  children: React.ReactNode;
  onRequestClose?: () => void;
}) {
  const classes = useStyles();
  const { t: tHeader } = useTranslation('header');
  const { t: tCommon } = useTranslation('common');
  const [expanded, setExpanded] = React.useState(false);
  const [signatureLogoUrl, setSignatureLogoUrl] = useState<string | null>(null);
  const { shop, shops } = useContext(AppStateContext);
  const isMounted = useIsMounted();

  const dispatch: Dispatch<iStoreAction> = useContext(AppDispatchContext);
  const history = useHistory();

  const loadShopSignatureLogo = useCallback(() => {
    if (!shop?.uid) {
      setSignatureLogoUrl(null);
      return;
    }
    new UserAPIs().fetch_user_signature().then((res) => {
      if (!isMounted.current) return;
      const u = res?.signature?.logo_url;
      setSignatureLogoUrl(typeof u === "string" && u.trim() !== "" ? u.trim() : null);
    });
  }, [shop?.uid, isMounted]);

  useEffect(() => {
    loadShopSignatureLogo();
  }, [loadShopSignatureLogo]);

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  function logout() {
    new UserAPIs().logout().then(() => {
      if (isMounted.current) {
        dispatch({ type: "logout" });
        onRequestClose?.();
        history.replace("/login");
      }
    });
  }

  function myAccount() {
    onRequestClose?.();
    history.push("/account");
  }

  return (
    <Card className={clsx(classes.root, 'ew-mobile-drawer-root')} elevation={0}>
      <CardContent className={clsx(classes.contentHead, 'ew-mobile-drawer__header')}>
        <div className="ew-mobile-drawer__header-stack">
          <Link
            to="/dashboard"
            className="ew-mobile-drawer__brand ew-mobile-drawer__brand--center"
            onClick={() => onRequestClose?.()}
          >
            <img
              src={logo}
              alt="Emailwish"
              className={clsx(classes.logoImage, "ew-mobile-drawer__logo-img")}
              style={{
                maxHeight: 38,
                maxWidth: 160,
                width: "75%",
                height: "auto",
                objectFit: "contain",
                display: "block",
                margin: "0 auto",
              }}
            />
          </Link>

          <div className="ew-mobile-drawer__store-wrap">
            <div className="ew-shop-switcher ew-mobile-drawer__shop-switcher">
              <button
                type="button"
                className="ew-shop-switcher__trigger"
                aria-haspopup="menu"
                aria-expanded={expanded}
                aria-controls="ew-mobile-drawer-shop-menu"
                id="ew-mobile-drawer-shop-trigger"
                onClick={handleExpandClick}
              >
                <span className="ew-shop-switcher__avatar-slot">
                  {shop ? (
                    <ShopSwitcherLogoOrInitials
                      name={shop.name}
                      colorKey={
                        shop.shop_owner_email ||
                        shop.myshopify_domain ||
                        String(shop.uid ?? "")
                      }
                      logoUrl={signatureLogoUrl}
                      variant="trigger"
                    />
                  ) : (
                    <span
                      className="ew-shop-switcher__initials ew-shop-switcher__initials--trigger ew-shop-switcher__initials--placeholder"
                      aria-hidden
                    >
                      ?
                    </span>
                  )}
                </span>
                <span className="ew-shop-switcher__name" title={shop?.name || undefined}>
                  {shop?.name || '—'}
                </span>
                <ExpandMore
                  className={classNames(
                    'ew-shop-switcher__chevron',
                    expanded && 'ew-shop-switcher__chevron--open',
                  )}
                  aria-hidden
                />
              </button>
            </div>
          </div>
        </div>
      </CardContent>

      <Collapse in={expanded} timeout="auto" unmountOnExit classes={{ root: classes.collapseRoot }}>
        <CardContent className={clsx(classes.shopListCollapseInner, 'ew-mobile-drawer__shop-list-panel')}>
          <nav
            id="ew-mobile-drawer-shop-menu"
            className="ew-dropdown-panel ew-dropdown-panel--shop-list ew-dropdown-panel--drawer-embed"
            role="menu"
            aria-labelledby="ew-mobile-drawer-shop-trigger"
          >
            {shops?.map((s: iShopifyShop) => {
              const active = shop?.uid === s.uid;
              const storeUrl = s.myshopify_domain && `https://${s.myshopify_domain}`;
              return (
                <div
                  key={s.uid || s.name}
                  className={classNames("ew-shop-switcher__row", active && "ew-shop-switcher__row--active")}
                  role="none"
                >
                  <button
                    type="button"
                    role="menuitem"
                    className="ew-shop-switcher__shop-btn"
                    onClick={async () => {
                      try {
                        const customerUid = s.customer_uid || s.uid;
                        await Axios.post(
                          process.env.REACT_APP_BACKEND_API_URL + `/switch/shop`,
                          { selected_customer_uid: customerUid },
                          { withCredentials: true }
                        );
                        invalidateUserSignatureCache();
                        clearProductCache();
                        // Header wins over cookie on subsequent API calls — update before refetch.
                        if (s.customer_uid) {
                          Axios.defaults.headers["X-Emailwish-Customer-UID"] = s.customer_uid;
                        }
                        const userRes = await new UserAPIs().fetch_user();
                        if (!UserAPIs.hasError(userRes)) {
                          await new CommonApis().after_login(userRes, dispatch);
                        }
                      } catch (err) {
                        console.error("Failed to switch shop", err);
                      }
                      setExpanded(false);
                      onRequestClose?.();
                    }}
                  >
                    <ShopSwitcherLogoOrInitials
                      name={s.name}
                      colorKey={
                        s.shop_owner_email ||
                        s.myshopify_domain ||
                        String(s.uid ?? "")
                      }
                      logoUrl={active ? signatureLogoUrl : null}
                      variant="list"
                    />
                    <span className="ew-shop-switcher__shop-name-text">{s.name}</span>
                  </button>
                  {storeUrl ? (
                    <a
                      href={storeUrl}
                      rel="noreferrer noopener"
                      target="_blank"
                      className="ew-shop-switcher__external"
                      aria-label={`Open ${s.name} storefront in new tab`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <OpenInNew fontSize="small" />
                    </a>
                  ) : (
                    <span className="ew-shop-switcher__external ew-shop-switcher__external--placeholder" aria-hidden />
                  )}
                </div>
              );
            })}
            <button
              type="button"
              role="menuitem"
              className="ew-dropdown-option ew-shop-switcher__add"
              onClick={() => {
                setExpanded(false);
                onRequestClose?.();
                history.push("/add-store");
              }}
            >
              <Add className="ew-shop-switcher__add-icon" fontSize="small" />
              {tCommon("shopSwitcher.addNewShop")}
            </button>
          </nav>
        </CardContent>
      </Collapse>

      <CardContent className={clsx(classes.content, 'ew-mobile-drawer__nav-wrap')}>
        {children}
      </CardContent>

      <div className={clsx(classes.footer, 'ew-mobile-drawer__footer')}>
        <Divider />
        <div className="ew-mobile-drawer__footer-inner">
          <ProduktlyFeedback />
          <ProduktlyNew />
          <NotificationPopupOver />
          <span className="ew-mobile-drawer__footer-language">
            <LanguageMenu menuAbove />
          </span>
          <span>
            <IconButton
              type="button"
              aria-label={tHeader('profile.labels.myAccount')}
              onClick={myAccount}
            >
              <AccountCircleOutlined />
            </IconButton>
          </span>
          <span>
            <IconButton
              type="button"
              aria-label={tHeader('profile.labels.logout')}
              onClick={logout}
            >
              <ExitToAppOutlined />
            </IconButton>
          </span>
        </div>
      </div>
    </Card>
  );
}
